import {
    Alert,
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    CssBaseline,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Toolbar,
    Tooltip,
    styled,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import { formatCurrency } from "../contabilidadSiigo/siigoHelpers";

const PAGE_SIZE = 20;

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

const ButtonPrincipal = styled(Button)({
    flexGrow: 0,
    borderRadius: "20px",
    gap: "10px",
    backgroundColor: "#161e55",
    color: "#ffff",
    margin: "0 0.5rem",
    "&:hover": {
        backgroundColor: "#A8C8AF",
    },
});

const ButtonEdit = styled(IconButton)({
    color: "#161e55",
    "&:hover": {
        color: "#A8C8AF",
    },
});

const TITLE_COLUMNS = [
    "ID Crédito",
    "Comercio",
    "Cliente",
    "Número documento",
    "Monto",
    "Estado",
    "Acciones",
];

const INVOICE_STATUS_LABELS = {
    validacion: "Validación",
};

const getInvoiceStatusLabel = (status) =>
    INVOICE_STATUS_LABELS[status] || status || "—";

const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

export default function ValidacionFacturas() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");

    const showSnackbar = (message, severity = "info") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosClient.get("/v1/commerce-invoices", {
                params: {
                    page,
                    size: PAGE_SIZE,
                    sort: "createdDate,asc",
                },
            });
            const data = response?.data;
            setRows(data?.content || []);
            setTotalElements(data?.totalElements ?? 0);
        } catch (error) {
            showSnackbar(
                getErrorMessage(error, "No fue posible cargar las facturas."),
                "error"
            );
            setRows([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const patchInvoice = async (invoiceUuid, action) => {
        setActionLoadingId(invoiceUuid);
        try {
            await axiosClient.patch(`/v1/commerce-invoices/${invoiceUuid}`, { action });
            showSnackbar(
                action === "approve"
                    ? "Factura validada correctamente."
                    : "Factura rechazada correctamente.",
                "success"
            );
            await fetchInvoices();
        } catch (error) {
            showSnackbar(
                getErrorMessage(error, "No fue posible actualizar la factura."),
                "error"
            );
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleApprove = (invoiceUuid) => {
        Swal.fire({
            title: "¿Desea validar esta factura?",
            showCancelButton: true,
            confirmButtonColor: "#161e55",
            cancelButtonColor: "#387A9C",
            confirmButtonText: "Validar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.isConfirmed) {
                patchInvoice(invoiceUuid, "approve");
            }
        });
    };

    const handleReject = (invoiceUuid) => {
        Swal.fire({
            title: "¿Desea rechazar esta factura?",
            showCancelButton: true,
            confirmButtonColor: "#161e55",
            cancelButtonColor: "#387A9C",
            confirmButtonText: "Rechazar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.isConfirmed) {
                patchInvoice(invoiceUuid, "reject");
            }
        });
    };

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    return (
        <>
            <CssBaseline />
            <Toolbar />
            <Box sx={{ width: "100%" }}>
                <Stack spacing={2}>
                    <Item>.</Item>
                    <Item />
                    <Grid container spacing={2}>
                        <Grid item>
                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                                <h1>Validación facturas</h1>
                                <ButtonPrincipal onClick={() => navigate("/homeAdmin/comercios")}>
                                    Volver a Comercios
                                </ButtonPrincipal>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="tabla validación facturas">
                                <TableHead>
                                    <TableRow>
                                        {TITLE_COLUMNS.map((title) => (
                                            <TableCell key={title} align="center">
                                                {title}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell align="center" colSpan={TITLE_COLUMNS.length}>
                                                Cargando contenido
                                            </TableCell>
                                        </TableRow>
                                    ) : rows.length > 0 ? (
                                        rows.map((row) => {
                                            const isActionLoading = actionLoadingId === row.invoiceUuid;
                                            return (
                                                <TableRow
                                                    key={row.invoiceUuid}
                                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                                >
                                                    <TableCell align="center">{row.creditId ?? "—"}</TableCell>
                                                    <TableCell align="center">{row.commerceName ?? "—"}</TableCell>
                                                    <TableCell align="center">{row.clientName ?? "—"}</TableCell>
                                                    <TableCell align="center">
                                                        {row.clientDocumentNumber ?? "—"}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {formatCurrency(row.creditAmount)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={getInvoiceStatusLabel(row.invoiceStatus)}
                                                            size="small"
                                                            color="info"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Stack direction="row" justifyContent="center" spacing={0.5}>
                                                            <Tooltip title="Ver detalle">
                                                                <ButtonEdit
                                                                    size="small"
                                                                    disabled={isActionLoading}
                                                                    onClick={() =>
                                                                        navigate(
                                                                            `/homeAdmin/comercios/facturas/${row.invoiceUuid}`
                                                                        )
                                                                    }
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </ButtonEdit>
                                                            </Tooltip>
                                                            <Tooltip title="Rechazar">
                                                                <ButtonEdit
                                                                    size="small"
                                                                    disabled={isActionLoading}
                                                                    onClick={() => handleReject(row.invoiceUuid)}
                                                                >
                                                                    {isActionLoading ? (
                                                                        <CircularProgress size={18} />
                                                                    ) : (
                                                                        <CancelIcon fontSize="small" />
                                                                    )}
                                                                </ButtonEdit>
                                                            </Tooltip>
                                                            <Tooltip title="Validar">
                                                                <ButtonEdit
                                                                    size="small"
                                                                    disabled={isActionLoading}
                                                                    onClick={() => handleApprove(row.invoiceUuid)}
                                                                >
                                                                    {isActionLoading ? (
                                                                        <CircularProgress size={18} />
                                                                    ) : (
                                                                        <CheckCircleIcon fontSize="small" />
                                                                    )}
                                                                </ButtonEdit>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell align="center" colSpan={TITLE_COLUMNS.length}>
                                                No hay facturas pendientes de validación
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                rowsPerPageOptions={[PAGE_SIZE]}
                                count={totalElements}
                                rowsPerPage={PAGE_SIZE}
                                page={page}
                                onPageChange={handleChangePage}
                            />
                        </TableContainer>
                    </Grid>
                </Stack>
            </Box>
            <Toolbar />
            <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}
